/**
 * One icon per module, shared by every surface that shows a module.
 *
 * This map used to live inside `my-bookings-page.tsx`, which meant the services list had
 * no way to draw a walk differently from a grooming appointment and fell back to printing
 * the raw enum. Keeping it beside `MODULE_LABEL` means a module gains its name and its
 * picture in the same edit.
 *
 * Aliases are deliberate: the schema has drifted (GROOMING/GROOMER, VET/CLINIC,
 * CAFE/RESTAURANT) and a surface should not have to know which spelling it was handed.
 */
import {
  Cat,
  Flame,
  FlaskConical,
  GraduationCap,
  Heart,
  PawPrint,
  Pill,
  Scissors,
  Stethoscope,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';

export const MODULE_ICON: Record<string, LucideIcon> = {
  VET: Stethoscope,
  CLINIC: Stethoscope,
  PHARMACY: Pill,
  FOOD: PawPrint,
  LAB: FlaskConical,
  TRAINER: GraduationCap,
  WALKER: PawPrint,
  GROOMING: Scissors,
  GROOMER: Scissors,
  ADOPTION: Heart,
  CREMATION: Flame,
  CAFE: UtensilsCrossed,
  RESTAURANT: UtensilsCrossed,
  BOARDING: Cat,
};

/** Fallback for a module the map has not caught up with yet. */
export function module_icon(module_type: string): LucideIcon {
  return MODULE_ICON[module_type] ?? PawPrint;
}

/**
 * A tint per module, so an unphotographed service is still scannable at a glance.
 *
 * Backgrounds are written as `bg-[hsl(var(--info)/0.12)]` rather than the shorter
 * `bg-info/12`. They are NOT equivalent here: `tailwind.config.js` declares these tokens
 * as `hsl(var(--info))` with no `<alpha-value>` slot, so Tailwind cannot thread an opacity
 * modifier through them and `bg-info/12` compiles to no background at all — the tile came
 * out fully transparent. The arbitrary form sets the alpha itself and works regardless.
 * (The same silent no-op affects every `bg-<token>` opacity utility elsewhere in the app;
 * fixing it globally means
 * changing the shared colour config, which repaints far more than this list.)
 */
export const MODULE_TINT: Record<string, string> = {
  VET: 'bg-[hsl(var(--info)/0.12)] text-info',
  CLINIC: 'bg-[hsl(var(--info)/0.12)] text-info',
  PHARMACY: 'bg-[hsl(var(--info)/0.12)] text-info',
  LAB: 'bg-[hsl(var(--info)/0.12)] text-info',
  TRAINER: 'bg-[hsl(var(--primary)/0.12)] text-primary',
  WALKER: 'bg-[hsl(var(--success)/0.12)] text-success',
  GROOMING: 'bg-[hsl(var(--warning)/0.18)] text-warning',
  GROOMER: 'bg-[hsl(var(--warning)/0.18)] text-warning',
  ADOPTION: 'bg-[hsl(var(--primary)/0.12)] text-primary',
  CREMATION: 'bg-muted text-muted-foreground',
  CAFE: 'bg-[hsl(var(--warning)/0.18)] text-warning',
  RESTAURANT: 'bg-[hsl(var(--warning)/0.18)] text-warning',
  BOARDING: 'bg-[hsl(var(--success)/0.12)] text-success',
};

export function module_tint(module_type: string): string {
  return MODULE_TINT[module_type] ?? 'bg-muted text-muted-foreground';
}
